/* ==========================================================================
   WennyVisual Premium — header behavior

   Custom elements:
     <announcement-bar>  rotator / marquee lifecycle
     <site-header>       navigation panels, scroll state, overlay coordination
     <mobile-menu>       side navigation drawer
     <search-overlay>    editorial search with predictive results

   Shared helpers come from theme.js via window.Theme.
   ========================================================================== */

(() => {
  const Theme = window.Theme || {};
  const { scrollLock, trapFocus, reveal } = Theme;
  const DESKTOP = '(min-width: 990px)';
  const RECENT_KEY = 'wv:recent-searches';
  const RECENT_MAX = 5;

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------------- */

  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.rotator = this.querySelector('[data-rotator]');
      this.track = this.querySelector('[data-announcement-track]');
      this.group = this.querySelector('[data-announcement-group]');

      if (this.dataset.layout === 'marquee' && this.track && this.group) {
        this.onMarqueeResize = this.onMarqueeResize.bind(this);
        this.rebuildMarquee = this.rebuildMarquee.bind(this);
        this.rebuildMarquee();
        window.addEventListener('resize', this.onMarqueeResize, { passive: true });
        if (typeof ResizeObserver !== 'undefined') {
          this.ro = new ResizeObserver(() => this.onMarqueeResize());
          this.ro.observe(this);
          this.ro.observe(this.group);
        }
        return;
      }

      if (!this.rotator) return;

      this.slides = [...this.rotator.querySelectorAll('[data-slide]')];
      if (this.slides.length < 2) return;

      this.index = 0;
      this.interval = Number(this.dataset.rotateInterval) || 5000;
      this.autoplay = this.dataset.autoplay === 'true';
      this.prevBtn = this.querySelector('[data-announcement-prev]');
      this.nextBtn = this.querySelector('[data-announcement-next]');

      this.onPrev = this.onPrev.bind(this);
      this.onNext = this.onNext.bind(this);
      this.onEnter = this.onEnter.bind(this);
      this.onLeave = this.onLeave.bind(this);

      this.prevBtn?.addEventListener('click', this.onPrev);
      this.nextBtn?.addEventListener('click', this.onNext);
      this.addEventListener('mouseenter', this.onEnter);
      this.addEventListener('mouseleave', this.onLeave);
      this.addEventListener('focusin', this.onEnter);
      this.addEventListener('focusout', this.onLeave);

      if (this.autoplay) this.start();
    }

    disconnectedCallback() {
      this.stop();
      this.prevBtn?.removeEventListener('click', this.onPrev);
      this.nextBtn?.removeEventListener('click', this.onNext);
      this.removeEventListener('mouseenter', this.onEnter);
      this.removeEventListener('mouseleave', this.onLeave);
      this.removeEventListener('focusin', this.onEnter);
      this.removeEventListener('focusout', this.onLeave);
      window.removeEventListener('resize', this.onMarqueeResize);
      this.ro?.disconnect();
      window.clearTimeout(this._marqueeTimer);
    }

    onMarqueeResize() {
      window.clearTimeout(this._marqueeTimer);
      this._marqueeTimer = window.setTimeout(this.rebuildMarquee, 100);
    }

    clearMarqueeClones() {
      this.track?.querySelectorAll('[data-announcement-clone]').forEach((node) => node.remove());
    }

    rebuildMarquee() {
      if (!this.track || !this.group) return;

      this.clearMarqueeClones();
      this.track.style.animation = 'none';
      void this.track.offsetWidth;

      const groupWidth = this.group.getBoundingClientRect().width;
      const viewport = this.getBoundingClientRect().width || window.innerWidth;

      if (groupWidth < 1) {
        this.style.setProperty('--announcement-loop-distance', '0px');
        return;
      }

      const needed = Math.max(2, Math.ceil((viewport * 2.5) / groupWidth) + 1);
      for (let i = 1; i < needed; i += 1) {
        const clone = this.group.cloneNode(true);
        clone.removeAttribute('data-announcement-group');
        clone.setAttribute('data-announcement-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
        this.track.appendChild(clone);
      }

      this.style.setProperty('--announcement-loop-distance', `${groupWidth}px`);
      void this.track.offsetWidth;
      this.track.style.animation = '';
    }

    onPrev(event) {
      event.preventDefault();
      this.go(-1);
      this.restart();
    }

    onNext(event) {
      event.preventDefault();
      this.go(1);
      this.restart();
    }

    onEnter() {
      this.stop();
    }

    onLeave() {
      if (this.autoplay) this.start();
    }

    restart() {
      this.stop();
      if (this.autoplay) this.start();
    }

    start() {
      if (this.timer || prefersReducedMotion() || !this.autoplay) return;
      this.timer = window.setInterval(() => this.go(1), this.interval);
    }

    stop() {
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = null;
    }

    go(step) {
      if (!this.slides?.length) return;
      this.slides[this.index].classList.remove('is-active');
      this.slides[this.index].setAttribute('aria-hidden', 'true');
      this.index = (this.index + step + this.slides.length) % this.slides.length;
      this.slides[this.index].classList.add('is-active');
      this.slides[this.index].removeAttribute('aria-hidden');
    }
  }

  /* ---------------------------------------------------------------------- */

  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.desktop = window.matchMedia(DESKTOP);
      this.isOverlay = this.dataset.overlay === 'true';
      this.pins = this.dataset.pin === 'true';
      this.watchesScroll = this.pins || this.dataset.scrollBehavior === 'solid';

      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onBreakpoint = this.onBreakpoint.bind(this);
      this.onOverlayChange = this.onOverlayChange.bind(this);

      this.addEventListener('click', this.onClick);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('theme:overlay', this.onOverlayChange);
      this.desktop.addEventListener('change', this.onBreakpoint);

      if (this.watchesScroll) {
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.onScroll();
      }
    }

    /* Soft-nav keeps this node. Overlay is computed from this header's
       settings + destination template — never copied from fetched header-group
       markup (Shopify often serves a cached homepage header). */
    applyPageState({ overlay = false, sticky = false, pin = false } = {}) {
      this.isOverlay = Boolean(overlay);
      this.pins = Boolean(pin);
      this.dataset.overlay = this.isOverlay ? 'true' : 'false';
      this.dataset.sticky = sticky ? 'true' : 'false';
      this.dataset.pin = this.pins ? 'true' : 'false';
      if (this.isOverlay && sticky) this.dataset.scrollBehavior = 'solid';
      else this.removeAttribute('data-scroll-behavior');

      this.classList.toggle('header--overlay', this.isOverlay);
      this.classList.toggle('transparent-header-active', this.isOverlay);
      const variant = this.dataset.logoVariant || 'light';
      this.classList.toggle('header--transparent-logo-light', this.isOverlay && variant === 'light');
      this.classList.toggle('header--transparent-logo-dark', this.isOverlay && variant !== 'light');
      this.classList.remove('is-solid', 'is-pinned', 'is-open');

      const root = document.documentElement;
      root.classList.toggle('has-transparent-header', this.isOverlay);
      root.classList.toggle('has-header-sticky', Boolean(sticky));
      root.classList.toggle('has-header-overlay-pin', Boolean(this.isOverlay && sticky));
      root.classList.toggle('has-desktop-drawer', this.dataset.desktopDrawer === 'true');

      this.watchesScroll = this.pins || this.dataset.scrollBehavior === 'solid';
      window.removeEventListener('scroll', this.onScroll);
      if (this.watchesScroll) {
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.onScroll();
      }
    }

    syncFromPage() {
      /* Overlay must not be copied from fetched header-group markup. */
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('focusout', this.onFocusOut);
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('theme:overlay', this.onOverlayChange);
      window.removeEventListener('scroll', this.onScroll);
      this.desktop.removeEventListener('change', this.onBreakpoint);
    }

    onClick(event) {
      const panelToggle = event.target.closest('[data-panel-toggle]');
      if (panelToggle) {
        event.preventDefault();
        event.stopPropagation();
        this.togglePanel(panelToggle);
        return;
      }

      if (event.target.closest('[data-menu-toggle]')) {
        document.getElementById('MobileMenu')?.toggle();
        return;
      }

      if (event.target.closest('[data-search-open]')) {
        event.preventDefault();
        document.getElementById('SearchOverlay')?.open();
        return;
      }

      if (event.target.closest('[data-wishlist-open]')) {
        event.preventDefault();
        document.getElementById('WishlistOverlay')?.open();
      }
    }

    onPointerOver() {}

    onPointerOut() {}

    onKeydown(event) {
      if (event.key !== 'Escape') return;
      const open = this.querySelector('.nav__item.is-open');
      if (!open) return;
      this.closePanel(open);
      open.querySelector('[data-panel-toggle]')?.focus();
    }

    onDocumentClick(event) {
      if (!this.contains(event.target)) this.closePanels();
    }

    onFocusOut(event) {
      const item = event.target.closest('.nav__item--has-panel');
      if (item && !item.contains(event.relatedTarget)) this.closePanel(item);
    }

    /* Sticky + transparent: solid fill after a short scroll.
       Static + transparent: stay transparent (Hero / Slideshow / Scroll Hero).
       Side drawers lock scroll — ignore the fake scrollY=0 jump or the header
       paints solid for a beat and then clears. */
    onScroll() {
      if (document.documentElement.classList.contains('is-scroll-locked')) return;
      const scrolled = window.scrollY;
      if (this.dataset.scrollBehavior === 'solid') {
        this.classList.toggle('is-solid', scrolled > 8);
      } else {
        this.classList.remove('is-solid');
      }

      /* An overlay header scrolls away with the announcement bar, then pins. */
      if (this.pins) {
        const offset = parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue('--announcement-height')
        ) || 0;
        this.classList.toggle('is-pinned', window.scrollY >= offset);
      }
    }

    onBreakpoint(event) {
      if (this.dataset.desktopDrawer === 'true') {
        if (!event.matches) this.closePanels();
        return;
      }
      if (event.matches) document.getElementById('MobileMenu')?.close();
      else this.closePanels();
    }

    /* Side drawers + fullscreen search/wishlist keep transparent header chrome.
       Only scrolled `.is-solid` should resolve to the solid palette. */
    onOverlayChange(event) {
      const id = event.detail.id;
      const keepOverlayChrome = id === 'CartDrawer' || id === 'MobileMenu' || id === 'SearchOverlay' || id === 'WishlistOverlay';
      if (!keepOverlayChrome) {
        this.classList.toggle('is-open', Boolean(event.detail.open));
      }

      if (event.detail.open) this.closePanels();
    }

    playPanelReveal(item) {
      const group = item?.querySelector('[data-reveal-group]');
      if (!group) return;
      reveal?.reset(group);
      window.requestAnimationFrame(() => reveal?.play(group));
    }

    togglePanel(button) {
      const item = button.closest('.nav__item');
      const willOpen = button.getAttribute('aria-expanded') !== 'true';
      this.closePanels();
      if (!willOpen || !item) return;
      const toggle = item.querySelector('[data-panel-toggle]');
      if (!toggle) return;
      toggle.setAttribute('aria-expanded', 'true');
      item.classList.add('is-open');
      this.playPanelReveal(item);
    }

    openPanel(item) {
      if (!item || item.classList.contains('is-open')) return;
      this.closePanels();
      const button = item.querySelector('[data-panel-toggle]');
      if (!button) return;
      button.setAttribute('aria-expanded', 'true');
      item.classList.add('is-open');
      this.playPanelReveal(item);
    }

    closePanel(item) {
      item.classList.remove('is-open');
      item.querySelector('[data-panel-toggle]')?.setAttribute('aria-expanded', 'false');
      reveal?.reset(item.querySelector('[data-reveal-group]'));
    }

    closePanels() {
      this.querySelectorAll('.nav__item.is-open').forEach((item) => this.closePanel(item));
    }
  }

  /* ---------------------------------------------------------------------- */

  /* Shared open/close plumbing for the mobile menu and the search overlay. */
  class Overlay extends HTMLElement {
    setup({ panel, toggles = [] } = {}) {
      this.panel = panel;
      this.toggles = toggles;
      this.onKeydown = this.onKeydown.bind(this);
      this.addEventListener('click', (event) => {
        if (event.target.closest(this.closeSelector)) this.close();
      });
    }

    /* The Theme Editor can replace the header while an overlay is open —
       never leave the page scroll-locked. */
    disconnectedCallback() {
      if (!this.isOpen) return;
      scrollLock?.unlock();
      this.releaseFocus?.();
      document.removeEventListener('keydown', this.onKeydown);
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open() {
      if (this.isOpen) return;
      document.dispatchEvent(new CustomEvent('theme:overlay:request', { detail: { source: this } }));
      this.lastFocused = document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      this.toggles.forEach((el) => el.setAttribute('aria-expanded', 'true'));
      scrollLock?.lock();
      this.releaseFocus = trapFocus?.(this.panel);
      document.addEventListener('keydown', this.onKeydown);
      document.dispatchEvent(new CustomEvent('theme:overlay', { detail: { open: true, id: this.id } }));
      this.afterOpen?.();
    }

    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      this.toggles.forEach((el) => el.setAttribute('aria-expanded', 'false'));
      scrollLock?.unlock();
      this.releaseFocus?.();
      document.removeEventListener('keydown', this.onKeydown);
      document.dispatchEvent(new CustomEvent('theme:overlay', { detail: { open: false, id: this.id } }));
      this.lastFocused?.focus({ preventScroll: true });
      this.afterClose?.();
    }

    toggle() {
      if (this.isOpen) this.close();
      else this.open();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        this.close();
      }
    }
  }

  /* ---------------------------------------------------------------------- */

  class MobileMenu extends Overlay {
    closeSelector = '[data-menu-close]';

    connectedCallback() {
      this.setup({
        panel: this.querySelector('.mobile-menu__panel'),
        toggles: [...document.querySelectorAll('[data-menu-toggle]')]
      });

      this.addEventListener('click', (event) => {
        const toggle = event.target.closest('[data-submenu-toggle]');
        if (toggle) this.toggleSubmenu(toggle);

        /* Search replaces the menu rather than stacking on top of it. */
        if (event.target.closest('[data-search-open]')) {
          event.preventDefault();
          this.close();
          document.getElementById('SearchOverlay')?.open();
          return;
        }

        if (event.target.closest('[data-wishlist-open]')) {
          event.preventDefault();
          this.close();
          document.getElementById('WishlistOverlay')?.open();
          return;
        }

        /* Hash / same-page links do not unload the document — close the drawer
           so scroll-lock cannot desync from an open menu. */
        const navLink = event.target.closest('a[href]');
        if (navLink) this.close();
      });
    }

    afterOpen() {
      this.syncDropdowns();
      reveal?.play(this.querySelector('.mobile-menu__body'));
      window.requestAnimationFrame(() => this.revealOpenDropdowns());
      this.panel?.focus({ preventScroll: true });
    }

    afterClose() {
      reveal?.reset(this);
      if (this.dataset.dropdowns === 'expanded') return;
      this.querySelectorAll('[data-submenu-item].is-open').forEach((item) => {
        item.classList.remove('is-open');
        item.querySelector(':scope > [data-submenu-toggle]')?.setAttribute('aria-expanded', 'false');
      });
    }

    syncDropdowns() {
      if (this.dataset.dropdowns !== 'expanded') return;
      this.querySelectorAll('[data-submenu-item]').forEach((item) => {
        item.classList.add('is-open');
        item.querySelector(':scope > [data-submenu-toggle]')?.setAttribute('aria-expanded', 'true');
      });
    }

    revealOpenDropdowns() {
      this.querySelectorAll('[data-submenu-item].is-open').forEach((item) => {
        const group = item.querySelector(':scope > .mobile-nav__panel [data-reveal-group]');
        if (!group) return;
        reveal?.reset(group);
        window.requestAnimationFrame(() => reveal?.play(group));
      });
    }

    toggleSubmenu(button) {
      const item = button.closest('[data-submenu-item]');
      if (!item) return;
      const open = button.getAttribute('aria-expanded') !== 'true';
      button.setAttribute('aria-expanded', String(open));
      item.classList.toggle('is-open', open);
      const group = item.querySelector('[data-reveal-group]');
      if (open) {
        reveal?.reset(group);
        window.requestAnimationFrame(() => reveal?.play(group));
      } else {
        reveal?.reset(group);
      }
    }
  }

  /* ---------------------------------------------------------------------- */

  class SearchOverlay extends Overlay {
    closeSelector = '[data-search-close]';

    connectedCallback() {
      this.setup({
        panel: this.querySelector('.search-overlay__panel'),
        toggles: [...document.querySelectorAll('[data-search-open]')]
      });

      this.form = this.querySelector('[data-search-form]');
      this.input = this.querySelector('[data-search-input]');
      this.prompts = this.querySelector('[data-search-prompts]');
      this.results = this.querySelector('[data-search-results]');
      this.status = this.querySelector('[data-search-status]');
      this.recentWrap = this.querySelector('[data-recent-searches]');
      this.recentList = this.querySelector('[data-recent-list]');
      this.predictive = this.dataset.predictive === 'true';
      this.limit = Number(this.dataset.resultsLimit) || 6;

      this.input?.addEventListener('input', () => this.onInput());
      this.form?.addEventListener('submit', () => this.remember(this.input.value));
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-clear-recent]')) this.clearRecent();
        else if (event.target.closest('[data-search-term]')) this.remember(event.target.textContent);
        else if (event.target.closest('a[href]')) this.remember(this.input.value);
      });

      this.onSlashKey = this.onSlashKey.bind(this);
      document.addEventListener('keydown', this.onSlashKey);
      this.renderRecent();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onSlashKey);
      super.disconnectedCallback();
    }

    onSlashKey(event) {
      if (event.key !== '/') return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) return;
      event.preventDefault();
      this.open();
    }

    afterOpen() {
      this.syncEmptyState();
      /* Reset then play for clean animation every time */
      this.prompts?.querySelectorAll('[data-reveal-group]').forEach((group) => {
        reveal?.reset(group);
        window.requestAnimationFrame(() => reveal?.play(group));
      });
      this.syncTypewriterVisibility();
      this.startTypewriter();
      /* Focus the panel — not the input — so the field doesn't look "active"
         (black border) until the shopper clicks or starts typing. */
      window.requestAnimationFrame(() => {
        try {
          this.panel?.focus({ preventScroll: true });
        } catch (error) {
          this.panel?.focus();
        }
      });
    }

    onKeydown(event) {
      super.onKeydown?.(event);
      if (!this.isOpen || !this.input) return;
      if (event.defaultPrevented) return;
      if (event.key === 'Escape' || event.key === 'Tab' || event.key === 'Enter') return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (document.activeElement === this.input) return;
      const target = event.target;
      if (target?.closest?.('a, button, input, textarea, select, [contenteditable="true"]')) return;

      if (event.key.length === 1) {
        event.preventDefault();
        this.input.focus({ preventScroll: true });
        const value = this.input.value || '';
        const start = this.input.selectionStart ?? value.length;
        const end = this.input.selectionEnd ?? value.length;
        this.input.value = `${value.slice(0, start)}${event.key}${value.slice(end)}`;
        const caret = start + event.key.length;
        try {
          this.input.setSelectionRange(caret, caret);
        } catch (error) {
          /* Some browsers reject setSelectionRange on type=search briefly. */
        }
        this.input.dispatchEvent(new Event('input', { bubbles: true }));
      } else if (event.key === 'Backspace') {
        event.preventDefault();
        this.input.focus({ preventScroll: true });
      }
    }

    startTypewriter() {
      const typewriter = this.querySelector('.search-overlay__typewriter');
      const wrap = this.input?.closest('.search-overlay__input-wrap');
      if (!typewriter || !wrap) return;

      window.clearTimeout(this.typewriterTimer);
      this.typewriterTimer = null;

      if ((this.input?.value || '').trim().length > 0) {
        wrap.classList.add('has-query');
        typewriter.textContent = '';
        typewriter.classList.remove('is-complete');
        return;
      }

      wrap.classList.remove('has-query', 'is-typing');
      const text = typewriter.getAttribute('data-typewriter-text') || typewriter.dataset.typewriterText || '';
      typewriter.textContent = '';
      typewriter.classList.remove('is-complete');

      if (!text) return;

      let i = 0;
      const type = () => {
        if (!this.classList.contains('is-open')) return;
        if ((this.input?.value || '').trim().length > 0) return;
        if (i < text.length) {
          typewriter.textContent = text.slice(0, i + 1);
          i += 1;
          this.typewriterTimer = window.setTimeout(type, 45);
        } else {
          typewriter.classList.add('is-complete');
        }
      };

      this.typewriterTimer = window.setTimeout(type, 120);
    }

    syncTypewriterVisibility() {
      const wrap = this.input?.closest('.search-overlay__input-wrap');
      if (!wrap) return;
      const hasQuery = (this.input?.value || '').trim().length > 0;
      wrap.classList.toggle('has-query', hasQuery);
      if (hasQuery) {
        window.clearTimeout(this.typewriterTimer);
        this.typewriterTimer = null;
      }
    }

    afterClose() {
      window.clearTimeout(this.typewriterTimer);
      this.typewriterTimer = null;
      this.abort?.abort();
      /* Reset all reveal groups so animation plays fresh on next open */
      this.prompts?.querySelectorAll('[data-reveal-group]').forEach((group) => reveal?.reset(group));
      if (this.results) reveal?.reset(this.results);
      if (this.recentList) reveal?.reset(this.recentList);
      this.renderRecent();
      this.syncEmptyState();
    }

    onInput() {
      const term = this.input.value.trim();
      window.clearTimeout(this.debounce);

      this.input.classList.toggle('has-query', term.length > 0);
      this.syncTypewriterVisibility();

      if (term.length < 2) {
        this.showPrompts();
        return;
      }

      if (!this.predictive) return;
      this.debounce = window.setTimeout(() => this.search(term), 300);
    }

    showPrompts() {
      if (this.prompts) this.prompts.hidden = false;
      if (this.results) this.results.innerHTML = '';
      if (this.status) this.status.textContent = '';
      this.syncEmptyState();
    }

    /* Keep the body visible while open so prompts and results have space. */
    syncEmptyState() {
      const hasPrompts =
        this.prompts &&
        !this.prompts.hidden &&
        [...this.prompts.children].some((child) => !child.hidden);
      const hasResults = Boolean(this.results?.firstElementChild);
      this.classList.toggle('has-content', Boolean(this.isOpen && (hasPrompts || hasResults)));
    }

    async search(term) {
      this.abort?.abort();
      this.abort = new AbortController();
      const { signal } = this.abort;

      const params = new URLSearchParams({
        q: term,
        section_id: 'predictive-search',
        'resources[type]': 'product',
        'resources[limit]': '8',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,vendor,tag,product_type,variants.title'
      });

      try {
        const response = await fetch(`${Theme.routes.predictive_search_url}?${params}`, {
          signal
        });
        if (!response.ok) throw new Error('Search failed');

        const html = await response.text();
        /* Drop late responses: abort may land after fetch resolved but before paint. */
        if (signal.aborted) return;
        if (term !== (this.input?.value || '').trim()) return;

        const incoming = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('.search-results');
        if (!incoming) return;

        if (this.prompts) this.prompts.hidden = true;
        this.results.replaceChildren(incoming);
        this.syncEmptyState();
        /* Reset then play for clean animation */
        const revealTarget = incoming.querySelector('[data-reveal-group]') || incoming;
        reveal?.reset(revealTarget);
        window.requestAnimationFrame(() => reveal?.play(revealTarget));

        const count = Number(incoming.dataset.resultCount || 0);
        if (this.status) {
          this.status.textContent = (Theme.strings.searchResults || '[count]').replace(
            '[count]',
            count
          );
        }
      } catch (error) {
        if (error.name !== 'AbortError' && this.status) {
          this.status.textContent = Theme.strings.searchError;
        }
      }
    }

    /* Recent searches — local only, never sent anywhere. */
    readRecent() {
      try {
        return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      } catch (error) {
        return [];
      }
    }

    remember(value) {
      const term = (value || '').trim();
      if (!this.recentWrap || term.length < 2) return;
      const next = [term, ...this.readRecent().filter((item) => item !== term)].slice(0, RECENT_MAX);
      try {
        localStorage.setItem(RECENT_KEY, JSON.stringify(next));
      } catch (error) {
        /* Storage blocked — recent searches simply do not persist. */
      }
    }

    clearRecent() {
      try {
        localStorage.removeItem(RECENT_KEY);
      } catch (error) {
        /* no-op */
      }
      this.renderRecent();
    }

    renderRecent() {
      if (!this.recentWrap || !this.recentList) return;
      const terms = this.readRecent();
      this.recentWrap.hidden = terms.length === 0;
      this.recentList.replaceChildren(
        ...terms.map((term, index) => {
          const link = document.createElement('a');
          link.className = 'search-overlay__term reveal reveal--up';
          link.href = `${Theme.routes.search_url}?q=${encodeURIComponent(term)}&type=product`;
          link.textContent = term;
          link.style.setProperty('--reveal-index', String(index));
          return link;
        })
      );
      this.syncEmptyState();
      if (this.isOpen) {
        reveal?.reset(this.recentList);
        window.requestAnimationFrame(() => reveal?.play(this.recentList));
      }
    }
  }

  /* ---------------------------------------------------------------------- */

class WishlistOverlay extends Overlay {
    closeSelector = '[data-wishlist-close]';

    connectedCallback() {
      this.setup({
        panel: this.querySelector('.wishlist-overlay__panel'),
        toggles: [...document.querySelectorAll('[data-wishlist-open]')]
      });
      this.empty = this.querySelector('[data-wishlist-empty]');
      this.grid = this.querySelector('[data-wishlist-grid]');
      this.toolbar = this.querySelector('[data-wishlist-toolbar]');
      this.onWishlistChange = this.onWishlistChange.bind(this);
      this.onClear = this.onClear.bind(this);
      document.addEventListener('theme:wishlist:change', this.onWishlistChange);
      this.addEventListener('click', this.onClear);
      this._renderToken = 0;
      this._cardCache = new Map();
      if (!this.readItems().length) this.showEmpty();
      else window.Theme?.Wishlist?.updateCount?.();
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      document.removeEventListener('theme:wishlist:change', this.onWishlistChange);
      this.removeEventListener('click', this.onClear);
    }

    onClear(event) {
      if (!event.target.closest('[data-wishlist-clear]')) return;
      event.preventDefault();
      this._cardCache.clear();
      window.Theme?.Wishlist?.clear?.();
      this.showEmpty();
    }

    afterOpen() {
      const items = this.readItems();
      if (!items.length) this.showEmpty();
      else this.render();
      this.panel?.focus({ preventScroll: true });
    }

    afterClose() {
      if (this.grid) reveal?.reset(this.grid);
    }

    onWishlistChange() {
      const items = this.readItems();
      if (!items.length) {
        this._cardCache.clear();
        this.showEmpty();
        return;
      }
      if (this.isOpen) this.render({ force: true });
    }

    rootUrl() {
      return window.Shopify?.routes?.root || '/';
    }

    readItems() {
      const api = window.Theme?.Wishlist;
      if (api?.read) return api.read();
      try {
        const raw = JSON.parse(localStorage.getItem('wv:wishlist') || '[]');
        return Array.isArray(raw) ? raw.filter((item) => item && (item.handle || item.id)) : [];
      } catch (error) {
        return [];
      }
    }

    handlesKey(handles) {
      return handles.join('\0');
    }

    currentHandles() {
      if (!this.grid) return [];
      return [...this.grid.querySelectorAll('[data-wishlist-handle]')]
        .map((el) => el.getAttribute('data-wishlist-handle') || '')
        .filter(Boolean);
    }

    playCards() {
      if (!this.grid) return;
      this.grid.querySelectorAll('.product-card-reveal, .reveal').forEach((el, index) => {
        const stagger = Math.min(index, 19);
        if (el.classList.contains('product-card--animation-domino') || el.classList.contains('product-card-reveal')) {
          el.style.setProperty('--card-reveal-index', String(stagger));
        }
        el.style.setProperty('--reveal-index', String(stagger));
      });
      reveal?.reset(this.grid);
      window.requestAnimationFrame(() => {
        void this.grid.offsetWidth;
        window.requestAnimationFrame(() => reveal?.play(this.grid));
      });
    }

    async render(options = {}) {
      const items = this.readItems();
      if (!items.length) {
        this._cardCache.clear();
        this.showEmpty();
        return;
      }

      const handles = items.map((item) => item.handle).filter(Boolean);
      const key = this.handlesKey(handles);
      if (
        !options.force &&
        this.grid &&
        !this.grid.hidden &&
        this.handlesKey(this.currentHandles()) === key &&
        this.grid.children.length
      ) {
        this.classList.remove('is-empty');
        if (this.empty) {
          this.empty.hidden = true;
          this.empty.setAttribute('hidden', '');
        }
        if (this.toolbar) this.toolbar.hidden = false;
        this.classList.add('has-content');
        this.playCards();
        return;
      }

      const token = ++this._renderToken;
      this.classList.remove('is-empty');
      if (this.empty) {
        this.empty.hidden = true;
        this.empty.setAttribute('hidden', '');
      }
      if (this.toolbar) this.toolbar.hidden = false;
      if (this.grid) this.grid.hidden = false;
      this.classList.add('has-content');

      const pairs = await Promise.all(
        handles.map(async (handle) => ({
          handle,
          html: await this.fetchCardHtml(handle).catch(() => '')
        }))
      );
      if (token !== this._renderToken || !this.isOpen) return;

      const ok = pairs.filter((pair) => pair.html);
      const bad = pairs.filter((pair) => !pair.html).map((pair) => pair.handle);
      if (bad.length) {
        bad.forEach((handle) => this._cardCache.delete(handle));
        window.Theme?.Wishlist?.pruneHandles?.(bad);
      }

      const markup = ok.map((pair) => pair.html).join('');
      if (!markup) {
        this.showEmpty();
        return;
      }

      this.paint(markup);
    }

    async fetchCardHtml(handle) {
      if (this._cardCache.has(handle)) return this._cardCache.get(handle);
      const url = `${this.rootUrl()}products/${encodeURIComponent(handle)}?section_id=wishlist-item`;
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) return '';
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const item = doc.querySelector('[data-wishlist-handle]');
      const markup = item ? item.outerHTML : '';
      if (markup) this._cardCache.set(handle, markup);
      return markup;
    }

    paint(markup) {
      const api = window.Theme?.Wishlist;
      this.classList.remove('is-empty');
      this.classList.add('has-content');
      if (this.toolbar) this.toolbar.hidden = false;
      if (this.empty) {
        this.empty.hidden = true;
        this.empty.setAttribute('hidden', '');
      }
      if (this.grid) {
        this.grid.hidden = false;
        this.grid.removeAttribute('hidden');
        this.grid.innerHTML = markup;
        this.grid.querySelectorAll('.reveal, .product-card-reveal').forEach((el) => {
          el.classList.remove('is-visible');
        });
        this.grid.querySelectorAll('[data-media-frame]').forEach((frame) => {
          frame.classList.remove('is-loading');
          frame.classList.add('is-loaded');
          frame.dataset.mediaReady = 'true';
        });
        api?.sync(this);
        window.MediaStates?.initAll?.(this.grid);
        this.grid.querySelectorAll('[data-drag-scroll]').forEach((node) => window.Theme?.bindDragScroll?.(node));
        this.playCards();
      }
    }

    showEmpty() {
      this.classList.add('is-empty');
      this.classList.add('has-content');
      if (this.toolbar) {
        this.toolbar.hidden = true;
        this.toolbar.setAttribute('hidden', '');
      }
      if (this.grid) {
        this.grid.innerHTML = '';
        this.grid.hidden = true;
        this.grid.setAttribute('hidden', '');
        reveal?.reset(this.grid);
      }
      if (this.empty) {
        this.empty.hidden = false;
        this.empty.removeAttribute('hidden');
      }
      window.Theme?.Wishlist?.updateCount?.();
    }
  }

  /* ---------------------------------------------------------------------- */

  /* Only one overlay may hold the scroll lock at a time. The cart drawer
     listens for the same event in theme.js. */
  document.addEventListener('theme:overlay:request', (event) => {
    const source = event.detail.source;
    ['MobileMenu', 'SearchOverlay', 'WishlistOverlay'].forEach((id) => {
      const el = document.getElementById(id);
      if (el && el !== source && el.isOpen) el.close();
    });
  });

  const define = (name, constructor) => {
    if (!customElements.get(name)) customElements.define(name, constructor);
  };

  define('announcement-bar', AnnouncementBar);
  define('site-header', SiteHeader);
  define('mobile-menu', MobileMenu);
  define('search-overlay', SearchOverlay);
  define('wishlist-overlay', WishlistOverlay);
})();
